'use client';

import { Suspense, useState, useEffect } from 'react';
import Link from 'next/link';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { useAuth } from '@/contexts/AuthContext';
import { AnimatedContainer } from '@/components/ui/animated-container';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { AppPageShell, PageHero, PageIntro, PageSection } from '@/components/site/page-shell';
import { useUrlTabState } from '@/hooks/use-url-tab-state';
import { getAuthProviderViaLabel } from '@/lib/authCopy';

const profileSchema = z.object({
  name: z.string().min(2, { message: 'Имя должно содержать минимум 2 символа' }),
  email: z.string().email({ message: 'Введите корректный адрес электронной почты' }),
});

const securitySchema = z.object({
  currentPassword: z.string().min(1, { message: 'Текущий пароль обязателен' }),
  newPassword: z.string().min(8, { message: 'Пароль должен содержать не менее 8 символов' }),
  confirmPassword: z.string(),
}).refine(data => data.newPassword === data.confirmPassword, {
  message: "Пароли не совпадают",
  path: ["confirmPassword"],
});

const notificationSchema = z.object({
  emailNotifications: z.boolean(),
  marketingEmails: z.boolean(),
  apiUsageAlerts: z.boolean(),
  securityAlerts: z.boolean(),
});

// Define a custom UserProfile type that includes notification preferences
type UserPreferences = {
  id: string;
  userId: string;
  emailNotifications: boolean;
  marketingEmails: boolean;
  apiUsageAlerts: boolean;
  securityAlerts: boolean;
  updatedAt: string;
};

type UserProfile = {
  id: string;
  name?: string | null;
  email?: string | null;
  pendingEmail?: string | null;
  image?: string | null;
  apiKey?: string | null;
  plan?: string | null;
  provider?: string | null;
  preferences?: UserPreferences | null;
};

type ProfileData = z.infer<typeof profileSchema>;
type SecurityData = z.infer<typeof securitySchema>;
type NotificationData = z.infer<typeof notificationSchema>;

function SettingsPageContent() {
  const { user: authUser, logout } = useAuth();
  const [user, setUser] = useState<UserProfile | null>(authUser as UserProfile);
  const [activeTab, setActiveTab] = useUrlTabState({
    allowedTabs: ['profile', 'security', 'notifications'] as const,
    defaultTab: 'profile',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [emailChangeRequested, setEmailChangeRequested] = useState(false);
  const { toast } = useToast();
  const providerViaLabel = getAuthProviderViaLabel(user?.provider);

  // Fetch the latest user data from the database
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setIsLoading(true);
        const response = await fetch('/api/user/profile');
        
        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
        } else {
          console.error('Failed to fetch user profile');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const profileForm = useForm<ProfileData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: '',
      email: '',
    },
  });

  const securityForm = useForm<SecurityData>({
    resolver: zodResolver(securitySchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  const notificationForm = useForm<NotificationData>({
    resolver: zodResolver(notificationSchema),
    defaultValues: {
      emailNotifications: true,
      marketingEmails: false,
      apiUsageAlerts: true,
      securityAlerts: true,
    },
  });

  // Update form values when user data is loaded
  useEffect(() => {
    if (!user) {
      return;
    }

    // If there's a pending email, show that in the form instead of the current email
    // This helps users understand that their email change is in progress
    profileForm.reset({
      name: user.name || '',
      email: user.pendingEmail || user.email || '',
    });

    // Initialize notification preferences form with user's existing preferences
    notificationForm.reset({
      emailNotifications: user.preferences?.emailNotifications ?? true,
      marketingEmails: user.preferences?.marketingEmails ?? false,
      apiUsageAlerts: user.preferences?.apiUsageAlerts ?? true,
      securityAlerts: user.preferences?.securityAlerts ?? true,
    });
  }, [user, profileForm, notificationForm]);

  // Function to refresh user profile data from the database
  const refreshUserProfile = async () => {
    try {
      const response = await fetch('/api/user/profile');
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
      }
    } catch (error) {
      console.error('Error refreshing profile:', error);
    }
  };

  const onProfileSubmit = async (data: ProfileData) => {
    setIsSubmitting(true);
    try {
      // Call the API to update profile information
      const response = await fetch('/api/user/update-profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось обновить профиль');
      }

      const result = await response.json();
      
      // Update the session to reflect the changes
      // This is a workaround since we can't directly update the session
      // In a real app, you might want to refresh the session or use a state management solution
      if (user) {
        user.name = data.name;
      }

      // Check if email change was initiated
      if (result.emailChangeInitiated) {
        setEmailChangeRequested(true);
        toast({
          title: 'Профиль обновлен',
          description: 'Проверьте новый адрес электронной почты, чтобы подтвердить изменение.',
        });
      } else {
        // Reset email change requested flag in case it was set previously
        setEmailChangeRequested(false);
        toast({
          title: 'Профиль обновлен',
          description: 'Изменения профиля сохранены',
        });
      }
      
      // Refresh user data to ensure we have the latest information
      await refreshUserProfile();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Не удалось обновить профиль',
        description: error.message || 'Не удалось обновить профиль',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSecuritySubmit = async (data: SecurityData) => {
    setIsSubmitting(true);
    try {
      // Call the API to update password
      const response = await fetch('/api/user/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          currentPassword: data.currentPassword,
          newPassword: data.newPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось обновить пароль');
      }

      securityForm.reset();
      toast({
        title: 'Пароль обновлен',
        description: 'Пароль успешно изменен',
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Не удалось обновить пароль',
        description: error.message || 'Не удалось обновить пароль',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onNotificationSubmit = async (data: NotificationData) => {
    setIsSubmitting(true);
    try {
      // Call the API to update notification preferences
      const response = await fetch('/api/user/update-notifications', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          emailNotifications: data.emailNotifications,
          marketingEmails: data.marketingEmails,
          apiUsageAlerts: data.apiUsageAlerts,
          securityAlerts: data.securityAlerts,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось обновить настройки уведомлений');
      }

      toast({
        title: 'Настройки уведомлений обновлены',
        description: 'Предпочтения по уведомлениям сохранены',
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Не удалось обновить уведомления',
        description: error.message || 'Не удалось обновить настройки уведомлений',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const [deletePassword, setDeletePassword] = useState('');
  const [deletePasswordError, setDeletePasswordError] = useState('');

  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      setDeletePasswordError('Введите пароль, чтобы подтвердить удаление аккаунта');
      return;
    }

    try {
      const response = await fetch('/api/user/delete-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: deletePassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось удалить аккаунт');
      }

      // Clear the password field
      setDeletePassword('');
      setDeletePasswordError('');
      
      // Log the user out
      logout();
      
      toast({
        title: 'Аккаунт помечен на удаление',
        description: 'Аккаунт помечен на удаление. Его можно восстановить в течение 30 дней по ссылке из письма.',
      });
      // Redirect to home page happens automatically after logout
    } catch (error: any) {
      setDeletePasswordError('');
      toast({
        variant: 'destructive',
        title: 'Не удалось удалить аккаунт',
        description: error.message || 'Не удалось удалить аккаунт',
      });
    }
  };

  return (
    <ProtectedRoute>
      <AppPageShell
        hero={
          <PageHero>
            <PageIntro
              eyebrow="Аккаунт"
              title="Настройки"
              description="Обновляйте профиль, пароль, уведомления и данные аккаунта."
              actions={
                <div className="flex flex-wrap gap-3">
                  <Link href="/billing">
                    <Button variant="outline">Биллинг</Button>
                  </Link>
                  <Link href="/api-keys">
                    <Button variant="outline">API-ключи</Button>
                  </Link>
                </div>
              }
            />
          </PageHero>
        }
      >
        <PageSection>
          <AnimatedContainer animation="fadeIn" className="space-y-8">

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="grid grid-cols-3 md:w-[460px]">
                <TabsTrigger value="profile">Профиль</TabsTrigger>
                <TabsTrigger value="security">Безопасность</TabsTrigger>
                <TabsTrigger value="notifications">Уведомления</TabsTrigger>
              </TabsList>

                <TabsContent value="profile">
                  <Card>
                    <CardHeader>
                      <CardTitle>Данные профиля</CardTitle>
                      <CardDescription>
                        Измените имя и адрес электронной почты
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <Form {...profileForm}>
                        <form onSubmit={profileForm.handleSubmit(onProfileSubmit)} className="space-y-6">
                          <FormField
                            control={profileForm.control}
                            name="name"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Имя</FormLabel>
                                <FormControl>
                                  <Input {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={profileForm.control}
                            name="email"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Электронная почта</FormLabel>
                                <FormControl>
                                  <Input {...field} disabled={user?.provider !== 'credentials'} />
                                </FormControl>
                                {user?.provider !== 'credentials' && (
                                  <FormDescription>
                                    Если вы входите через {providerViaLabel}, адрес электронной почты здесь изменить нельзя.
                                  </FormDescription>
                                )}
                                {user?.pendingEmail && (
                                  <FormDescription className="text-amber-600">
                                    Текущий адрес: {user.email} (обновится после подтверждения)
                                  </FormDescription>
                                )}
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          {emailChangeRequested && (
                            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4 rounded-md">
                              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                <strong>Смена адреса почти готова:</strong> откройте письмо на новом адресе и
                                подтвердите его. Изменение вступит в силу после подтверждения.
                              </p>
                            </div>
                          )}
                          
                          {user?.pendingEmail && !emailChangeRequested && (
                            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4 rounded-md">
                              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                <strong>Смена адреса уже запрошена:</strong> новый адрес {user.pendingEmail}. Откройте
                                письмо и подтвердите его.
                              </p>
                            </div>
                          )}

                          <Button
                            type="submit"
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Сохранение...' : 'Сохранить изменения'}
                          </Button>
                        </form>
                      </Form>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="security">
                  <Card>
                    <CardHeader>
                      <CardTitle>Настройки безопасности</CardTitle>
                      <CardDescription>
                        Измените пароль для входа в аккаунт
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {user?.provider === 'credentials' ? (
                        <Form {...securityForm}>
                          <form onSubmit={securityForm.handleSubmit(onSecuritySubmit)} className="space-y-6">
                            <FormField
                              control={securityForm.control}
                              name="currentPassword"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Текущий пароль</FormLabel>
                                  <FormControl>
                                    <Input type="password" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={securityForm.control}
                              name="newPassword"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Новый пароль</FormLabel>
                                  <FormControl>
                                    <Input type="password" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <FormField
                              control={securityForm.control}
                              name="confirmPassword"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Подтвердите новый пароль</FormLabel>
                                  <FormControl>
                                    <Input type="password" {...field} />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            <Button
                              type="submit"
                              className="w-full bg-blue-600 hover:bg-blue-700"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? 'Обновление...' : 'Обновить пароль'}
                            </Button>
                          </form>
                        </Form>
                      ) : (
                        <div className="p-6 bg-[var(--site-surface-inset)] rounded-lg text-center">
                          <p className="text-[var(--site-ink)]">
                            Управление паролем доступно только для входа по электронной почте и паролю.
                          </p>
                          <p className="text-[var(--site-ink-muted)] text-sm mt-2">
                            Если вы входите через {providerViaLabel}, меняйте настройки безопасности у этого сервиса.
                          </p>
                        </div>
                      )}

                      <div className="pt-6 border-t border-[color:var(--site-border-soft)] mt-8">
                        <h3 className="text-lg font-medium mb-4">Удалить аккаунт</h3>
                        <p className="text-[var(--site-ink-muted)] mb-4">
                          Аккаунт будет помечен на удаление и окончательно удален через 30 дней. До этого его можно
                          восстановить по ссылке из письма.
                        </p>

                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive">Удалить аккаунт</Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Удалить аккаунт?</AlertDialogTitle>
                              <AlertDialogDescription>
                                После подтверждения аккаунт будет помечен на удаление. В течение 30 дней его можно
                                восстановить по ссылке из письма.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            
                            {user?.provider === 'credentials' && (
                              <div className="py-4">
                                <div className="space-y-2">
                                  <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">Введите пароль для подтверждения</label>
                                  <Input 
                                    type="password" 
                                    value={deletePassword}
                                    onChange={(e) => setDeletePassword(e.target.value)}
                                    placeholder="Ваш текущий пароль"
                                  />
                                  {deletePasswordError && (
                                    <p className="text-sm font-medium text-red-500">{deletePasswordError}</p>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            <AlertDialogFooter>
                              <AlertDialogCancel onClick={() => {
                                setDeletePassword('');
                                setDeletePasswordError('');
                              }}>Отмена</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={handleDeleteAccount}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Удалить аккаунт
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="notifications">
                  <Card>
                    <CardHeader>
                      <CardTitle>Настройки уведомлений</CardTitle>
                      <CardDescription>
                        Выберите, какие уведомления вы хотите получать
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Form {...notificationForm}>
                        <form onSubmit={notificationForm.handleSubmit(onNotificationSubmit)} className="space-y-6">
                          <FormField
                            control={notificationForm.control}
                            name="emailNotifications"
                            render={({ field }) => (
                              <FormItem className="flex justify-between items-center space-y-0 rounded-md border p-4">
                                <div className="space-y-0.5">
                                  <FormLabel>Уведомления по электронной почте</FormLabel>
                                  <FormDescription>
                                    Получать уведомления по электронной почте
                                  </FormDescription>
                                </div>
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={notificationForm.control}
                            name="marketingEmails"
                            render={({ field }) => (
                              <FormItem className="flex justify-between items-center space-y-0 rounded-md border p-4">
                                <div className="space-y-0.5">
                                  <FormLabel>Маркетинговые письма</FormLabel>
                                  <FormDescription>
                                    Получать письма о новых функциях и предложениях
                                  </FormDescription>
                                </div>
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={notificationForm.control}
                            name="apiUsageAlerts"
                            render={({ field }) => (
                              <FormItem className="flex justify-between items-center space-y-0 rounded-md border p-4">
                                <div className="space-y-0.5">
                                  <FormLabel>Оповещения об использовании API</FormLabel>
                                  <FormDescription>
                                    Получать уведомления об использовании API и лимитах квоты
                                  </FormDescription>
                                </div>
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={notificationForm.control}
                            name="securityAlerts"
                            render={({ field }) => (
                              <FormItem className="flex justify-between items-center space-y-0 rounded-md border p-4">
                                <div className="space-y-0.5">
                                  <FormLabel>Оповещения безопасности</FormLabel>
                                  <FormDescription>
                                    Получать важные уведомления по безопасности
                                  </FormDescription>
                                </div>
                                <FormControl>
                                  <Switch
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <Button
                            type="submit"
                            className="w-full bg-blue-600 hover:bg-blue-700"
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? 'Сохранение...' : 'Сохранить настройки'}
                          </Button>
                        </form>
                      </Form>
                    </CardContent>
                  </Card>
                </TabsContent>
            </Tabs>
          </AnimatedContainer>
        </PageSection>
      </AppPageShell>
    </ProtectedRoute>
  );
}

export default function SettingsPage() {
  return (
    <Suspense fallback={<div className="p-8 text-center text-sm text-[var(--site-ink-muted)]">Загружаем настройки...</div>}>
      <SettingsPageContent />
    </Suspense>
  );
}
